import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import {
  Add,
  Close,
  Delete,
  EditOutlined,
  ImageOutlined,
  Refresh,
  Search,
  Visibility,
} from "@mui/icons-material";
import AppPagination from "components/AppPagination";
import ConfirmationDialog from "components/ConfirmationDialog";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { isSupportButtonAllowed } from "config/ConfigAccessButton";
import { usePageTitle } from "contexts/PageTitleContext";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import {
  notifySupportRequestCreated,
} from "utils/supportNotifications";

const MAX_PHOTOS = 3;
const DEFAULT_STATUSES = ["Pending", "In Progress", "Complete", "Done"];
const STATUS_FLOW = {
  Pending: ["In Progress"],
  "In Progress": ["Complete"],
  Complete: ["Done"],
  Done: [],
};
const pageSizeOptions = [10, 25, 50, 100];

const FilterCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  background: theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
}));

const FilterHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.25, 1.5),
  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  background: alpha(theme.palette.primary.main, 0.02),
  borderRadius: `${theme.spacing(1.5)} ${theme.spacing(1.5)} 0 0`,
}));

const StatusTabs = styled(Box)(() => ({
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  alignItems: "flex-end",
}));

const StatusTab = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  minHeight: 36,
  borderRadius: "8px 8px 0 0",
  textTransform: "none",
  fontWeight: 700,
  padding: "8px 14px",
  fontSize: "0.875rem",
  ...(active
    ? {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.primary.main,
        borderBottom: `2px solid ${theme.palette.primary.main}`,
      }
    : {
        color: theme.palette.text.secondary,
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          color: theme.palette.primary.main,
        },
      }),
}));

const getApiErrorMessage = (error, fallback = "Something went wrong") => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  if (error.message && typeof error.message === "string") return error.message;
  if (error.detail) return String(error.detail);
  if (error.error) return String(error.error);
  if (typeof error === "object") {
    const first = Object.values(error).flat?.()?.[0] || Object.values(error)[0];
    if (typeof first === "string") return first;
    if (Array.isArray(first) && first[0]) return String(first[0]);
  }
  return fallback;
};

const extractRows = (response) => {
  if (!response || typeof response !== "object") return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.results)) return response.results;
  if (Array.isArray(response.data)) return response.data;
  const data = response.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data.data)) return data.data;
    for (const key of ["support_requests", "tickets", "requests", "SupportRequest"]) {
      if (Array.isArray(data[key])) return data[key];
    }
    const firstArray = Object.values(data).find((v) => Array.isArray(v));
    if (firstArray) return firstArray;
  }
  return [];
};

const extractPagination = (response) => {
  if (!response || typeof response !== "object") return null;
  return (
    response.pagination ||
    response.meta ||
    (response.data &&
      typeof response.data === "object" &&
      !Array.isArray(response.data) &&
      response.data.pagination) ||
    null
  );
};

const extractDetail = (response) => {
  if (!response || typeof response !== "object") return null;
  if (response.data && typeof response.data === "object" && !Array.isArray(response.data)) {
    return response.data;
  }
  return response;
};

const normalizeStatusChoices = (response) => {
  const raw =
    response?.data?.statuses ||
    response?.data?.status_choices ||
    response?.data?.choices ||
    response?.statuses ||
    response?.status_choices ||
    response?.choices ||
    response?.data ||
    [];
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === "string") return item;
        return item?.name || item?.label || item?.value || item?.status || "";
      })
      .filter(Boolean);
  }
  if (raw && typeof raw === "object") {
    return Object.values(raw)
      .map((item) => (typeof item === "string" ? item : item?.name || item?.label || ""))
      .filter(Boolean);
  }
  return [];
};

const getStatusColor = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "pending") return "warning";
  if (key === "in progress") return "info";
  if (key === "complete") return "success";
  if (key === "done") return "default";
  return "default";
};

const getTicketId = (ticket) =>
  ticket?.id ?? ticket?.support_id ?? ticket?.pk ?? ticket?.support_request_id;

const resolvePhotoUrls = (data) => {
  const photos = data?.photos || data?.images || [];
  if (!Array.isArray(photos)) return [];
  return photos
    .map((photo) => {
      if (typeof photo === "string") return apiService.resolveAuthenticatedFileUrl(photo);
      const url = photo?.url || photo?.photo || photo?.image || photo?.path || photo?.file;
      return url ? apiService.resolveAuthenticatedFileUrl(url) : null;
    })
    .filter(Boolean);
};

const emptyForm = () => ({
  title: "",
  description: "",
  photos: [],
  sources: [],
});

const Support = () => {
  const { setPageTitle } = usePageTitle();
  const toast = useToast();
  const [permissionTick, setPermissionTick] = useState(0);

  useEffect(() => {
    const handlePermissionsUpdated = () => setPermissionTick((value) => value + 1);
    window.addEventListener("permissions-updated", handlePermissionsUpdated);
    return () => window.removeEventListener("permissions-updated", handlePermissionsUpdated);
  }, []);

  // permissionTick forces a re-read of localStorage after permissions are saved
  const can = (action) => {
    void permissionTick;
    return isSupportButtonAllowed(action);
  };
  const canCreate = can("create");
  const canUpdate = can("update");
  const canView = can("view");
  const canDelete = can("delete");
  const canUpdateStatus = can("update_status") || canUpdate;
  const canViewAll = can("view_all");

  const [statusFilter, setStatusFilter] = useState("all");
  const [statusChoices, setStatusChoices] = useState(DEFAULT_STATUSES);
  const [searchValue, setSearchValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm());
  const [creating, setCreating] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [statusLogs, setStatusLogs] = useState([]);
  const [statusForm, setStatusForm] = useState({ status: "", remark: "" });
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyForm());
  const [editing, setEditing] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, ticket: null });
  const [deleting, setDeleting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState({ open: false, urls: [], loading: false });

  useEffect(() => {
    setPageTitle("Support Feature");
  }, [setPageTitle]);

  const fetchStatusChoices = useCallback(async () => {
    try {
      const response = await apiService.get(API_ENDPOINTS.GET_support_status_choices);
      const choices = normalizeStatusChoices(response);
      if (choices.length) setStatusChoices(choices);
    } catch (error) {
      console.error("Failed to load support status choices:", error);
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", String(currentPage));
      params.append("page_size", String(pageSize));
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (searchTerm.trim()) params.append("search", searchTerm.trim());
      if (canViewAll) params.append("all", "true");

      const response = await apiService.get(
        `${API_ENDPOINTS.GET_support_requests}?${params.toString()}`
      );
      const rows = extractRows(response);
      const pagination = extractPagination(response);

      setTickets(rows);
      if (pagination) {
        setTotalPages(
          Number(pagination.total_pages || pagination.num_pages || pagination.pages || 1) || 1
        );
        setTotalItems(
          Number(pagination.total_count || pagination.count || pagination.total || rows.length) ||
            rows.length
        );
      } else {
        const count = Number(response?.count || response?.data?.count || rows.length) || rows.length;
        setTotalItems(count);
        setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
      }
    } catch (error) {
      console.error("Failed to load support requests:", error);
      toast.showError(getApiErrorMessage(error, "Failed to load support requests"));
      setTickets([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [canViewAll, currentPage, pageSize, searchTerm, statusFilter, toast]);

  useEffect(() => {
    fetchStatusChoices();
  }, [fetchStatusChoices]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const statusTabs = useMemo(
    () => [{ value: "all", label: "All" }, ...statusChoices.map((s) => ({ value: s, label: s }))],
    [statusChoices]
  );

  const nextStatuses = useMemo(() => {
    const current = detail?.status || "";
    if (Object.prototype.hasOwnProperty.call(STATUS_FLOW, current)) {
      return STATUS_FLOW[current];
    }
    return statusChoices.filter((s) => s !== current);
  }, [detail?.status, statusChoices]);

  const openCreate = () => {
    setCreateForm(emptyForm());
    setCreateOpen(true);
  };

  const handlePhotoSelect = (files, target = "create") => {
    const selected = Array.from(files || []);
    const setter = target === "edit" ? setEditForm : setCreateForm;
    const existingCount =
      target === "edit"
        ? Number(detail?.photo_count || detail?.photos?.length || 0) || 0
        : 0;
    setter((prev) => {
      const remaining = MAX_PHOTOS - existingCount - (prev.photos?.length || 0);
      if (remaining <= 0) {
        toast.showError(`Maximum ${MAX_PHOTOS} photos allowed`);
        return prev;
      }
      if (selected.length > remaining) {
        toast.showError(`Maximum ${MAX_PHOTOS} photos allowed`);
      }
      const nextFiles = selected.slice(0, remaining);
      const nextSources = nextFiles.map(() => "Gallery");
      return {
        ...prev,
        photos: [...(prev.photos || []), ...nextFiles],
        sources: [...(prev.sources || []), ...nextSources],
      };
    });
  };

  const removePhoto = (index, target = "create") => {
    const setter = target === "edit" ? setEditForm : setCreateForm;
    setter((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
      sources: prev.sources.filter((_, i) => i !== index),
    }));
  };

  const handleCreate = async () => {
    if (!createForm.title.trim() || !createForm.description.trim()) {
      toast.showError("Title and description are required");
      return;
    }
    setCreating(true);
    try {
      let response;
      if (createForm.photos.length > 0) {
        const formData = new FormData();
        formData.append("title", createForm.title.trim());
        formData.append("description", createForm.description.trim());
        createForm.photos.forEach((file) => formData.append("photos", file));
        createForm.sources.forEach((source) => formData.append("sources", source || "Gallery"));
        response = await apiService.postMultipart(
          API_ENDPOINTS.POST_create_support_request,
          formData
        );
      } else {
        response = await apiService.post(API_ENDPOINTS.POST_create_support_request, {
          title: createForm.title.trim(),
          description: createForm.description.trim(),
        });
      }

      if (response?.success === false) {
        throw response;
      }
      toast.showSuccess(response?.message || "Support request created");
      notifySupportRequestCreated({
        title: createForm.title.trim(),
        description: createForm.description.trim(),
      });
      setCreateOpen(false);
      setCreateForm(emptyForm());
      setCurrentPage(1);
      fetchTickets();
    } catch (error) {
      toast.showError(getApiErrorMessage(error, "Failed to create support request"));
    } finally {
      setCreating(false);
    }
  };

  const loadDetail = async (ticket) => {
    const supportId = getTicketId(ticket);
    if (!supportId) {
      toast.showError("Support request id not found");
      return;
    }
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    setStatusLogs([]);
    setStatusForm({ status: "", remark: "" });
    try {
      const [detailRes, logsRes] = await Promise.all([
        apiService.get(
          `${API_ENDPOINTS.GET_support_request_by_id}?support_id=${encodeURIComponent(supportId)}`
        ),
        apiService
          .get(
            `${API_ENDPOINTS.GET_support_status_logs}?support_id=${encodeURIComponent(supportId)}`
          )
          .catch(() => null),
      ]);
      const detailData = extractDetail(detailRes) || ticket;
      setDetail(detailData);
      const logsFromApi = extractRows(logsRes);
      const logsFromDetail = detailData?.status_logs || detailData?.logs || [];
      setStatusLogs(
        logsFromApi.length > 0
          ? logsFromApi
          : Array.isArray(logsFromDetail)
          ? logsFromDetail
          : []
      );
      const next = (STATUS_FLOW[detailData?.status] || [])[0] || "";
      setStatusForm({ status: next, remark: "" });
    } catch (error) {
      toast.showError(getApiErrorMessage(error, "Failed to load support request"));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    const supportId = getTicketId(detail);
    if (!supportId || !statusForm.status) {
      toast.showError("Select a status to update");
      return;
    }
    setUpdatingStatus(true);
    try {
      const response = await apiService.put(API_ENDPOINTS.PUT_update_support_status, {
        support_id: supportId,
        status: statusForm.status,
        remark: statusForm.remark.trim(),
      });
      if (response?.success === false) throw response;
      toast.showSuccess(response?.message || "Status updated");
      setDetailOpen(false);
      fetchTickets();
    } catch (error) {
      toast.showError(getApiErrorMessage(error, "Failed to update status"));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openEdit = () => {
    if (!detail) return;
    setEditForm({
      title: detail.title || "",
      description: detail.description || "",
      photos: [],
      sources: [],
    });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    const supportId = getTicketId(detail);
    if (!supportId) return;
    if (!editForm.title.trim() || !editForm.description.trim()) {
      toast.showError("Title and description are required");
      return;
    }
    setEditing(true);
    try {
      let response;
      const existingPhotos = Array.isArray(detail?.photos) ? detail.photos.length : Number(detail?.photo_count || 0);
      if (editForm.photos.length > 0) {
        const formData = new FormData();
        formData.append("support_id", supportId);
        formData.append("title", editForm.title.trim());
        formData.append("description", editForm.description.trim());
        editForm.photos.forEach((file) => formData.append("photos", file));
        editForm.sources.forEach((source) => formData.append("sources", source || "Gallery"));
        if (existingPhotos + editForm.photos.length > MAX_PHOTOS) {
          toast.showError(`Maximum ${MAX_PHOTOS} photos allowed`);
          setEditing(false);
          return;
        }
        response = await apiService.putMultipart(
          API_ENDPOINTS.PUT_update_support_request,
          formData
        );
      } else {
        response = await apiService.put(API_ENDPOINTS.PUT_update_support_request, {
          support_id: supportId,
          title: editForm.title.trim(),
          description: editForm.description.trim(),
        });
      }
      if (response?.success === false) throw response;
      toast.showSuccess(response?.message || "Support request updated");
      setEditOpen(false);
      await loadDetail(detail);
      fetchTickets();
    } catch (error) {
      toast.showError(getApiErrorMessage(error, "Failed to update support request"));
    } finally {
      setEditing(false);
    }
  };

  const handleDelete = async () => {
    const ticket = deleteDialog.ticket;
    const supportId = getTicketId(ticket);
    if (!supportId) return;
    setDeleting(true);
    try {
      const response = await apiService.delete(
        `${API_ENDPOINTS.DELETE_support_request}?support_id=${encodeURIComponent(supportId)}`
      );
      if (response?.success === false) throw response;
      toast.showSuccess(response?.message || "Support request deleted");
      setDeleteDialog({ open: false, ticket: null });
      if (getTicketId(detail) === supportId) {
        setDetailOpen(false);
        setDetail(null);
      }
      fetchTickets();
    } catch (error) {
      toast.showError(getApiErrorMessage(error, "Failed to delete support request"));
    } finally {
      setDeleting(false);
    }
  };

  const openTicketPhotos = async (ticket) => {
    const photoCount = Number(ticket.photo_count ?? ticket.photos?.length ?? 0);
    if (photoCount <= 0) {
      toast.showError("No photos available");
      return;
    }

    let urls = resolvePhotoUrls(ticket);
    if (urls.length === 0) {
      const supportId = getTicketId(ticket);
      if (!supportId) {
        toast.showError("Support request id not found");
        return;
      }

      setPhotoPreview({ open: true, urls: [], loading: true });
      try {
        const detailRes = await apiService.get(
          `${API_ENDPOINTS.GET_support_request_by_id}?support_id=${encodeURIComponent(supportId)}`
        );
        const detailData = extractDetail(detailRes) || ticket;
        urls = resolvePhotoUrls(detailData);
      } catch (error) {
        toast.showError(getApiErrorMessage(error, "Failed to load photos"));
        setPhotoPreview({ open: false, urls: [], loading: false });
        return;
      }
    } else {
      setPhotoPreview({ open: true, urls, loading: false });
      return;
    }

    if (!urls.length) {
      toast.showError("No photos available");
      setPhotoPreview({ open: false, urls: [], loading: false });
      return;
    }

    setPhotoPreview({ open: true, urls, loading: false });
  };

  const photoUrls = useMemo(() => resolvePhotoUrls(detail), [detail]);

  const isPending = String(detail?.status || "").toLowerCase() === "pending";

  return (
    <Box pt={1.5} pb={3}>
      <FilterCard>
        <FilterHeader>
          <FlexBetween gap={1} flexWrap="wrap">
            <Typography variant="subtitle2" fontWeight={700} color="primary">
              Filter & Search
            </Typography>
            <FlexBox alignItems="center" gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={fetchTickets}
                disabled={loading}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Refresh
              </Button>
              <Tooltip title={canCreate ? "Create Request" : "No permission to create request"}>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={openCreate}
                    disabled={!canCreate}
                    sx={{ textTransform: "none", fontWeight: 600, opacity: !canCreate ? 0.6 : 1 }}
                  >
                    Create Request
                  </Button>
                </span>
              </Tooltip>
            </FlexBox>
          </FlexBetween>

          <Card
            sx={{
              px: 1.25,
              py: 0.75,
              mt: 1.25,
              borderRadius: 1.5,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <StatusTabs>
              {statusTabs.map((tab) => (
                <StatusTab
                  key={tab.value}
                  active={statusFilter === tab.value}
                  onClick={() => {
                    setStatusFilter(tab.value);
                    setCurrentPage(1);
                  }}
                >
                  {tab.label}
                </StatusTab>
              ))}
            </StatusTabs>
          </Card>
        </FilterHeader>

        <Box sx={{ px: 1.5, py: 1.25 }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={8}>
              <FlexBox alignItems="center" gap={1}>
                <SearchInput
                  placeholder="Search by title or description..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setSearchTerm(searchValue);
                      setCurrentPage(1);
                    }
                  }}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    border: "1px solid",
                    borderColor: "grey.300",
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: "background.paper",
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={() => {
                    setSearchTerm(searchValue);
                    setCurrentPage(1);
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    height: 40,
                    minWidth: 96,
                    whiteSpace: "nowrap",
                  }}
                >
                  Search
                </Button>
              </FlexBox>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Rows</InputLabel>
                <Select
                  label="Rows"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {pageSizeOptions.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </FilterCard>

      <Card
        sx={{
          marginTop: 1.25,
          borderRadius: 1.5,
          overflow: "hidden",
          border: "1px solid",
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <TableContainer>
          <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap",
                    bgcolor: "grey.200",
                    py: 1.5,
                    px: 1,
                    width: 80,
                    minWidth: 80,
                    maxWidth: 80,
                  }}
                >
                  Sr. No.
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap",
                    bgcolor: "grey.200",
                    py: 1.5,
                    px: 2,
                    width: "32%",
                  }}
                >
                  Title
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap",
                    bgcolor: "grey.200",
                    py: 1.5,
                    px: "16px !important",
                    width: 120,
                    minWidth: 120,
                    textAlign: "center !important",
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap",
                    bgcolor: "grey.200",
                    py: 1.5,
                    px: "16px !important",
                    width: 100,
                    minWidth: 100,
                    textAlign: "center !important",
                  }}
                >
                  Photos
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap",
                    bgcolor: "grey.200",
                    py: 1.5,
                    px: "16px !important",
                    width: 180,
                    minWidth: 180,
                    textAlign: "center !important",
                  }}
                >
                  Created
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap",
                    bgcolor: "grey.200",
                    py: 1.5,
                    pl: "16px !important",
                    pr: "40px !important",
                    width: 110,
                    minWidth: 110,
                    maxWidth: 110,
                    textAlign: "left !important",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ border: "none" }}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight={220}
                    >
                      <CircularProgress size={36} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : tickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ border: "none" }}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight={220}
                    >
                      <Typography color="text.secondary">
                        No support requests found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                tickets.map((ticket, index) => {
                  const id = getTicketId(ticket);
                  const serialNo = (currentPage - 1) * pageSize + index + 1;
                  return (
                    <TableRow
                      key={id}
                      hover
                      sx={{
                        "&:hover": {
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                        },
                        "& > .MuiTableCell-root": {
                          borderBottom: (theme) =>
                            `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          py: 1.5,
                          px: 2,
                          verticalAlign: "middle",
                        },
                      }}
                    >
                      <TableCell
                        align="center"
                        sx={{
                          whiteSpace: "nowrap",
                          width: 80,
                          minWidth: 80,
                          maxWidth: 80,
                          px: "8px !important",
                          textAlign: "center",
                        }}
                      >
                        {serialNo}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={600} noWrap sx={{ maxWidth: 360 }}>
                          {ticket.title || "—"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ display: "block", maxWidth: 360 }}
                        >
                          {ticket.description || ""}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          whiteSpace: "nowrap",
                          width: 120,
                          minWidth: 120,
                          px: "16px !important",
                          textAlign: "center !important",
                        }}
                      >
                        <Chip
                          size="small"
                          label={ticket.status || "—"}
                          color={getStatusColor(ticket.status)}
                        />
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          whiteSpace: "nowrap",
                          width: 100,
                          minWidth: 100,
                          px: "16px !important",
                          textAlign: "center !important",
                        }}
                      >
                        <FlexBox alignItems="center" justifyContent="center" gap={0.5}>
                          <Tooltip
                            title={
                              (ticket.photo_count ?? ticket.photos?.length ?? 0) > 0
                                ? "View photos"
                                : "No photos"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={(ticket.photo_count ?? ticket.photos?.length ?? 0) <= 0}
                                onClick={() => openTicketPhotos(ticket)}
                                sx={{ p: 0.25 }}
                              >
                                <ImageOutlined fontSize="small" color="action" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Typography variant="body2">
                            {ticket.photo_count ?? ticket.photos?.length ?? 0}
                          </Typography>
                        </FlexBox>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          whiteSpace: "nowrap",
                          width: 180,
                          minWidth: 180,
                          px: "16px !important",
                          textAlign: "center !important",
                        }}
                      >
                        {ticket.created_at
                          ? new Date(ticket.created_at).toLocaleString()
                          : ticket.created_on
                          ? new Date(ticket.created_on).toLocaleString()
                          : "—"}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          whiteSpace: "nowrap",
                          width: 110,
                          minWidth: 110,
                          maxWidth: 110,
                          pl: "16px !important",
                          pr: "40px !important",
                          textAlign: "left !important",
                        }}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            transform: "translateX(-8px)",
                          }}
                        >
                          <Tooltip title={canView ? "View" : "No permission to view"}>
                            <span>
                              <IconButton
                                size="small"
                                color="viewBlue"
                                onClick={() => {
                                  if (!canView) return;
                                  loadDetail(ticket);
                                }}
                                disabled={!canView}
                                sx={{ opacity: !canView ? 0.5 : 1 }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={canDelete ? "Delete" : "No permission to delete"}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  if (!canDelete) return;
                                  setDeleteDialog({ open: true, ticket });
                                }}
                                disabled={!canDelete}
                                sx={{ opacity: !canDelete ? 0.5 : 1 }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {!loading && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {totalItems > 0
              ? `Showing ${(currentPage - 1) * pageSize + 1} - ${Math.min(
                  currentPage * pageSize,
                  totalItems
                )} of ${totalItems} requests`
              : "Showing 0 of 0 requests"}
          </Typography>
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <AppPagination
                shape="rounded"
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          )}
        </Box>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Support Request</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Title"
              required
              fullWidth
              value={createForm.title}
              onChange={(e) => setCreateForm((p) => ({ ...p, title: e.target.value }))}
            />
            <TextField
              label="Description"
              required
              fullWidth
              multiline
              minRows={4}
              value={createForm.description}
              onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
            />
            <Box>
              <Button
                variant="outlined"
                component="label"
                disabled={createForm.photos.length >= MAX_PHOTOS}
                sx={{ textTransform: "none" }}
              >
                Add Photos ({createForm.photos.length}/{MAX_PHOTOS})
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    handlePhotoSelect(e.target.files, "create");
                    e.target.value = "";
                  }}
                />
              </Button>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                {createForm.photos.map((file, index) => (
                  <Chip
                    key={`${file.name}-${index}`}
                    label={file.name}
                    onDelete={() => removePhoto(index, "create")}
                    size="small"
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => !updatingStatus && setDetailOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Support Request Details
          <IconButton onClick={() => setDetailOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detail ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              <FlexBetween>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    {detail.title}
                  </Typography>
                  {/* <Typography variant="body2" color="text.secondary">
                    ID: {getTicketId(detail)}
                  </Typography> */}
                </Box>
                <Chip label={detail.status || "—"} color={getStatusColor(detail.status)} />
              </FlexBetween>

              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                {detail.description}
              </Typography>

              {photoUrls.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Photos
                  </Typography>
                  <Grid container spacing={1}>
                    {photoUrls.map((url, index) => (
                      <Grid item xs={6} sm={4} key={`${url}-${index}`}>
                        <Box
                          component="a"
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          sx={{
                            display: "block",
                            borderRadius: 1,
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: "divider",
                            height: 140,
                          }}
                        >
                          <Box
                            component="img"
                            src={url}
                            alt={`Support photo ${index + 1}`}
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              <Divider />

              {(isPending) && (
                <Box>
                  <Tooltip title={canUpdate ? "Edit Request" : "No permission to update"}>
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<EditOutlined />}
                        onClick={openEdit}
                        disabled={!canUpdate}
                        sx={{ textTransform: "none", opacity: !canUpdate ? 0.6 : 1 }}
                      >
                        Edit Request
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              )}

              {canUpdateStatus && nextStatuses.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Update Status
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          label="Status"
                          value={statusForm.status}
                          onChange={(e) =>
                            setStatusForm((p) => ({ ...p, status: e.target.value }))
                          }
                        >
                          {nextStatuses.map((status) => (
                            <MenuItem key={status} value={status}>
                              {status}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Remark"
                        value={statusForm.remark}
                        onChange={(e) =>
                          setStatusForm((p) => ({ ...p, remark: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={updatingStatus || !statusForm.status}
                        onClick={handleUpdateStatus}
                        sx={{ textTransform: "none", height: 40 }}
                      >
                        {updatingStatus ? <CircularProgress size={18} /> : "Update"}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Status Logs
                </Typography>
                {statusLogs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No status logs yet
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {statusLogs.map((log, index) => (
                      <Box
                        key={log.id || index}
                        sx={{
                          p: 1.25,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: alpha("#000", 0.02),
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {log.status || log.new_status || log.to_status || "Status updated"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {log.remark || log.comment || log.note || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[
                            log.updated_by_name ||
                              log.updated_by ||
                              log.user_name ||
                              log.created_by,
                            (() => {
                              const rawDate =
                                log.created_at || log.updated_at || log.timestamp;
                              if (!rawDate) return null;
                              const parsed = new Date(rawDate);
                              return Number.isNaN(parsed.getTime())
                                ? rawDate
                                : parsed.toLocaleString();
                            })(),
                          ]
                            .filter(Boolean)
                            .join(" • ")}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => !editing && setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Support Request</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Title"
              required
              fullWidth
              value={editForm.title}
              onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
            />
            <TextField
              label="Description"
              required
              fullWidth
              multiline
              minRows={4}
              value={editForm.description}
              onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
            />
            <Box>
              <Button
                variant="outlined"
                component="label"
                disabled={
                  (Number(detail?.photo_count || detail?.photos?.length || 0) || 0) +
                    editForm.photos.length >=
                  MAX_PHOTOS
                }
                sx={{ textTransform: "none" }}
              >
                Add Photos (
                {(Number(detail?.photo_count || detail?.photos?.length || 0) || 0) +
                  editForm.photos.length}
                /{MAX_PHOTOS})
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    handlePhotoSelect(e.target.files, "edit");
                    e.target.value = "";
                  }}
                />
              </Button>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                {editForm.photos.map((file, index) => (
                  <Chip
                    key={`${file.name}-${index}`}
                    label={file.name}
                    onDelete={() => removePhoto(index, "edit")}
                    size="small"
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditOpen(false)} disabled={editing}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleEdit} disabled={editing}>
            {editing ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={photoPreview.open}
        onClose={() => setPhotoPreview({ open: false, urls: [], loading: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <FlexBetween>
            Photos
            <IconButton
              size="small"
              onClick={() => setPhotoPreview({ open: false, urls: [], loading: false })}
            >
              <Close />
            </IconButton>
          </FlexBetween>
        </DialogTitle>
        <DialogContent dividers>
          {photoPreview.loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Grid container spacing={1.5}>
              {photoPreview.urls.map((url, index) => (
                <Grid item xs={12} sm={6} md={4} key={`${url}-${index}`}>
                  <Box
                    component="img"
                    src={url}
                    alt={`Support photo ${index + 1}`}
                    sx={{
                      width: "100%",
                      maxHeight: 280,
                      objectFit: "cover",
                      borderRadius: 1,
                      border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={deleteDialog.open}
        onClose={() => !deleting && setDeleteDialog({ open: false, ticket: null })}
        onConfirm={handleDelete}
        title="Delete Support Request"
        message="Are you sure you want to delete this support request? This is a soft delete."
        confirmButtonText={deleting ? "Deleting..." : "Delete"}
        confirmButtonColor="error"
        isLoading={deleting}
      />
    </Box>
  );
};

export default Support;
